import type {
  AcademyConfirmedBy,
  AcademyLesson,
  AcademyArea,
} from '../../../pages/academy/Academy/AcademyService';
import { isRequiredProgressComplete } from './academyLabels';

export interface TierSkillCompletionCount {
  completedSkills: number;
  skillCount: number;
  percent: number;
}

export function getTierSkills(area: AcademyArea, tierKey: string): AcademyLesson[] {
  const tierSkills = new Map<string, AcademyLesson>();

  area.skillPaths.forEach((path) => {
    path.skills.forEach((skill) => {
      if (skill.tierKeys.includes(tierKey) && !tierSkills.has(skill.skillKey)) {
        tierSkills.set(skill.skillKey, skill);
      }
    });
  });

  return Array.from(tierSkills.values());
}

export function getTierCompletionCount(
  area: AcademyArea,
  tierKey: string,
): TierSkillCompletionCount {
  const skills = getTierSkills(area, tierKey);
  const completedSkills = skills.filter(isSkillComplete).length;
  const skillCount = skills.length;

  return {
    completedSkills,
    skillCount,
    percent: skillCount === 0 ? 0 : (completedSkills / skillCount) * 100,
  };
}

export function isSkillComplete(skill: AcademyLesson): boolean {
  return isRequiredProgressComplete(
    skill.learnConfirmedBy,
    skill.applyConfirmedBy,
    skill.learnRequired,
    skill.applyRequired,
  );
}

export function getBestAcademyConfirmedBy(
  currentConfirmedBy: AcademyConfirmedBy,
  nextConfirmedBy: AcademyConfirmedBy,
): AcademyConfirmedBy {
  return getAcademyConfirmedByRank(nextConfirmedBy) > getAcademyConfirmedByRank(currentConfirmedBy)
    ? nextConfirmedBy
    : currentConfirmedBy;
}

export function getBestAcademyCompletedAt(
  confirmedBy: AcademyConfirmedBy,
  currentConfirmedBy: AcademyConfirmedBy,
  currentCompletedAt: string | null,
  nextCompletedAt: string | null,
): string | null {
  if (confirmedBy === 'none') {
    return null;
  }

  return confirmedBy === currentConfirmedBy
    ? getLatestAcademyCompletedAt(currentCompletedAt, nextCompletedAt)
    : nextCompletedAt || currentCompletedAt;
}

export function isAcademyConfirmedBy(value: unknown): value is AcademyConfirmedBy {
  return value === 'none' || value === 'self' || value === 'system';
}

function getLatestAcademyCompletedAt(
  currentCompletedAt: string | null,
  nextCompletedAt: string | null,
): string | null {
  if (!currentCompletedAt || !nextCompletedAt) {
    return currentCompletedAt || nextCompletedAt;
  }

  return Date.parse(nextCompletedAt) > Date.parse(currentCompletedAt)
    ? nextCompletedAt
    : currentCompletedAt;
}

export function isTierComplete(area: AcademyArea, tierKey: string): boolean {
  const skills = getTierSkills(area, tierKey);

  return skills.length > 0 && skills.every(isSkillComplete);
}

export function isTierAvailable(area: AcademyArea, tierIndex: number): boolean {
  return area.tiers
    .slice(0, tierIndex)
    .every((tier) => isTierEmpty(area, tier.tierKey) || isTierComplete(area, tier.tierKey));
}

function isTierEmpty(area: AcademyArea, tierKey: string): boolean {
  return getTierSkills(area, tierKey).length === 0;
}

function getAcademyConfirmedByRank(confirmedBy: AcademyConfirmedBy): number {
  if (confirmedBy === 'system') {
    return 2;
  }

  return confirmedBy === 'self' ? 1 : 0;
}
